import type { ScenarioContent } from 'shared';
import { mockScenario as phoneDemo } from '../mockScenario';
import hajimeteData from './hajimeteNoIrai.json';
import liquidStreetWalkText from '../../../../docs/prod/demo_scenario_liquid_street_walk.json?raw';
import qaAllMinigamesText from '../../../../seed/scenarios/qa_all_minigames.json?raw';

/** Phase A-k: 「はじめての依頼」— 世界観ベースのデモシナリオ */
export const hajimeteNoIrai: ScenarioContent = hajimeteData as unknown as ScenarioContent;

/** Phase A-j: 「付喪神の電話」— 機能デモシナリオ */
export const tsukumogamiPhone: ScenarioContent = phoneDemo;

/** Phase A-m: Mr.リキッド街歩き検証デモ */
export const liquidStreetWalk: ScenarioContent = JSON.parse(liquidStreetWalkText) as ScenarioContent;

/** Phase C QA: 実19本のミニゲーム（templateId 1-9, 11-20）+ 動画 step 通し検証用 */
export const qaAllMinigames: ScenarioContent = JSON.parse(qaAllMinigamesText) as ScenarioContent;

/**
 * ローカル検証専用配線の吸収（literal glob なので local.ts が無ければ空のまま）。
 * `vite build` では exclude-local-scenarios plugin が空モジュールへ差し替えるため本番に混入しない。
 */
const localModules = import.meta.glob<{ default: Record<string, ScenarioContent> }>('./local.ts', { eager: true });
const localScenarios: Record<string, ScenarioContent> = localModules['./local.ts']?.default ?? {};

/**
 * 営業デモ配線の吸収（コミット対象・詳細は ./demo.ts ヘッダ）。
 * `vite build` では exclude-demo-scenarios plugin が空スタブへ差し替えるため本番に混入しない。
 * `VITE_INCLUDE_DEMO=1` を立てた build のときだけ同梱される（dev では常に有効）。
 */
const demoModules = import.meta.glob<{ default: Record<string, ScenarioContent> }>('./demo.ts', { eager: true });
const demoScenarios: Record<string, ScenarioContent> = demoModules['./demo.ts']?.default ?? {};

/**
 * P9-2 Part C: `?scenario=` の解決結果（正規化 id + シナリオ本体）。
 * id はエイリアス（`liquid`/`liquid-street-walk`/`mr-liquid` 等）を 1 つの正規名に潰した安定識別子で、
 * 進行保存キー `player_progress_<id>` に使う（旧実装は全シナリオ共通 'mock-scenario' で衝突していた）。
 */
export interface ScenarioSelection {
  id: string;
  scenario: ScenarioContent;
}

/**
 * URLパラメータ `?scenario=` に応じたデモシナリオを正規化 id 付きで返す。
 * - `phone` → 付喪神の電話（Phase A-j）
 * - `liquid-street-walk` / `liquid` → Mr.リキッド街歩き検証デモ（Phase A-m）
 * - `qa-all` → QA: 全ミニゲーム通し検証（Phase C 受入試験用）
 * - ローカル検証配線は ./local.ts（gitignore 対象・local.example.ts 参照）。id は param そのまま
 * - 営業デモ配線は ./demo.ts（コミット対象・build は VITE_INCLUDE_DEMO=1 のときのみ同梱）。id は param そのまま
 * - デフォルト → はじめての依頼（Phase A-k）
 */
export function resolveScenarioByParam(param: string | null): ScenarioSelection {
  switch (param) {
    case 'phone':
      return { id: 'phone', scenario: tsukumogamiPhone };
    case 'liquid':
    case 'liquid-street-walk':
    case 'mr-liquid':
      return { id: 'liquid-street-walk', scenario: liquidStreetWalk };
    case 'qa-all':
    case 'qa':
    case 'qa-all-minigames':
      return { id: 'qa-all-minigames', scenario: qaAllMinigames };
    default: {
      const localHit = param ? localScenarios[param] : undefined;
      if (param && localHit) {
        return { id: param, scenario: localHit };
      }
      const demoHit = param ? demoScenarios[param] : undefined;
      if (param && demoHit) {
        return { id: param, scenario: demoHit };
      }
      return { id: 'hajimete-no-irai', scenario: hajimeteNoIrai };
    }
  }
}

/** 従来 API（シナリオ本体のみ）。挙動不変・resolveScenarioByParam へ委譲。 */
export function getScenarioByParam(param: string | null): ScenarioContent {
  return resolveScenarioByParam(param).scenario;
}
